import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import type { DocumentItem } from '@document-explorer/core';
import type { ActionDef } from './types.js';
import { visibleActions } from './actions.js';
import { DeUiIconComponent, type UiIconName } from './ui-icon.component.js';

const BUILT_IN_ICONS: Record<string, UiIconName> = { view: 'eye', download: 'download' };

@Component({
  selector: 'de-actions-menu',
  standalone: true,
  imports: [DeUiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (available().length > 0) {
      <div class="de-actions" (click)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
        <button
          type="button"
          class="de-action-button"
          aria-haspopup="menu"
          [attr.aria-expanded]="open()"
          [attr.aria-label]="'Actions for ' + item().name"
          (click)="open.set(!open())"
        >
          <de-ui-icon name="more" />
        </button>
        @if (open()) {
          <div class="de-menu" role="menu">
            @for (action of available(); track action.id) {
              <button
                type="button"
                role="menuitem"
                class="de-menu-item"
                [class.de-menu-item--danger]="action.danger"
                (click)="run(action)"
              >
                @if (iconFor(action.id); as icon) {
                  <span class="de-icon" aria-hidden="true"><de-ui-icon [name]="icon" /></span>
                }
                {{ action.label }}
              </button>
            }
          </div>
        }
      </div>
    } @else {
      <span class="de-actions"></span>
    }
  `,
})
export class DeActionsMenuComponent {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly item = input<DocumentItem>({ id: '', name: '', type: 'file' });
  readonly actions = input<ActionDef[]>([]);
  readonly action = output<{ actionId: string; item: DocumentItem }>();

  protected readonly open = signal(false);
  protected readonly available = computed(() => visibleActions(this.actions(), this.item()));

  protected iconFor(id: string): UiIconName | null {
    return BUILT_IN_ICONS[id] ?? null;
  }

  protected run(action: ActionDef): void {
    this.open.set(false);
    this.action.emit({ actionId: action.id, item: this.item() });
  }

  @HostListener('document:mousedown', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.open.set(false);
  }
}
